import { useEffect, useRef } from "react";
import JsBarcode from "jsbarcode";
import { Button, Modal } from "@shop-ledger/ui-components";

export interface LabelPrintModalProps {
  open: boolean;
  onClose: () => void;
  itemName: string;
  priceLabel: string;
  barcodeValue: string;
}

/**
 * Business-owner request: "generate labels for some products with no barcodes ... print
 * barcode and product labels so they can print on sticker paper and apply on their
 * products." Renders a Code128 barcode (via `jsbarcode`, the standard zero-dependency JS
 * barcode-rendering library — this repo's only barcode-*generation* capability; scanning
 * already existed via `html5-qrcode` in `pos-pwa`) plus the product name/price, sized for
 * a common small sticker label.
 *
 * "Print / Save as PDF" is `window.print()` scoped to `.sl-print-label` by the
 * `@media print` rule in `index.css` — the exact same isolation technique
 * `pos-pwa`'s `ReceiptPreviewModal.tsx` uses, so there is one printing mechanism across
 * this app family, not two.
 */
export function LabelPrintModal({ open, onClose, itemName, priceLabel, barcodeValue }: LabelPrintModalProps) {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!open || !svgRef.current) return;
    JsBarcode(svgRef.current, barcodeValue, {
      format: "CODE128",
      width: 2,
      height: 50,
      fontSize: 14,
      margin: 6,
    });
  }, [open, barcodeValue]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Print label"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button type="button" variant="primary" onClick={() => window.print()}>
            Print / Save as PDF
          </Button>
        </>
      }
    >
      <div className="sl-print-label">
        <div className="sl-print-label__name">{itemName}</div>
        <svg ref={svgRef} />
        <div className="sl-print-label__price">{priceLabel}</div>
      </div>
    </Modal>
  );
}
